import { useMutation, useQueryClient } from '@tanstack/react-query';

import { useLibraryPostsGateway } from './LibraryPostsGatewayProvider';
import { applyEngagementChange } from '../api/libraryPostCache';
import type { EngageableLibraryPost } from '../model/types';
import { useLibraryFilters } from '../state/libraryFiltersStore';

import { type SortOption } from '@/constants/community';
import { useUserScope } from '@/hooks/useUserScope';
import { trackCommunityPostSaveToggled } from '@/util/analyticsEvents';

type UseToggleSavedPostParams = {
  /**
   * `undefined` while the post hasn't loaded yet. `toggleSaved` is a no-op
   * until it resolves. Accepting `undefined` lets a caller that renders a
   * toggle button only once data exists (e.g. a `Stack.Toolbar.Button`,
   * which must be this hook's direct JSX sibling rather than hidden behind
   * a wrapper component) still call this hook unconditionally, keeping it
   * in a stable position for the Rules of Hooks.
   */
  item: EngageableLibraryPost | undefined;
  cancerTypeId?: number;
  sortBy?: SortOption;
  /**
   * The post screen's `q` URL param, if any. Must match whatever the caller
   * fetched the post with, since the post's cache key includes it, or the
   * optimistic update lands on an entry the screen isn't reading from.
   */
  q?: string;
  /** Where the save happened, so saves started from Research read apart from the Library's. */
  source?: 'community' | 'library_chat';
};

/**
 * The save/unsave toggle for an Inquiry Library post, shared by the Library
 * feed's action bar and Research's result viewer.
 *
 * The list it updates is whatever Library list the reader is in. A caller
 * outside the Library (Research) has no such list cached, so only the post
 * itself updates, which every caller needs regardless of where it was opened.
 */
export function useToggleSavedPost({
  item,
  cancerTypeId,
  sortBy,
  q,
  source,
}: UseToggleSavedPostParams) {
  const queryClient = useQueryClient();

  const { userId } = useUserScope();

  const gateway = useLibraryPostsGateway();

  const searchQuery = useLibraryFilters((state) => state.searchQuery) || undefined;

  const showSavedPosts = useLibraryFilters((state) => state.showSavedPosts) || undefined;

  const postId = item ? Number(item.id) : undefined;

  const saved = Boolean(item?.saved);

  const { mutate: setSaved } = useMutation({
    mutationFn: (variables: { postId: number; saved: boolean }) =>
      gateway.setSaved(variables.postId, variables.saved),
    onMutate: (variables) =>
      applyEngagementChange(queryClient, {
        userId,
        postId: variables.postId,
        change: { kind: 'save', removeFromSavedView: Boolean(saved && showSavedPosts) },
        view: { cancerTypeId, searchQuery, showSavedPosts, sortBy },
        postQ: q,
      }),
    onSuccess: (_data, variables) => {
      trackCommunityPostSaveToggled({
        postId: variables.postId,
        saved: variables.saved,
        source: source ?? 'community',
      });
    },
    onError: (error, variables, change) => {
      console.error(`useToggleSavedPost:setSaved:${JSON.stringify(variables)}:ERROR:`, error);

      change?.rollback();
    },
    onSettled: (_data, _error, _variables, change) => {
      change?.settle();
    },
  });

  const toggleSaved = () => {
    if (postId === undefined) {
      return;
    }

    setSaved({ saved: !saved, postId });
  };

  return { saved, toggleSaved };
}
